import React, { useEffect, useRef } from 'react';
import { ButtonV2Props } from 'libs/growth-platform-brand-system-v2/src/components/ButtonV2';
import { IImage } from 'libs/brand-system/src/types/module';
import { PayFitColor } from 'libs/growth-platform-brand-system-v2/src/types/module';

import {
  classNames,
  useScreenSize,
} from '@payfit/growth-platform-brand-system-v2';
import { SubMenuContent } from './SubMenuContent';

interface ISubmenuItem {
  __typename?: string;
  title: string;
  subtitle: string;
  gif: IImage;
  image: IImage;
  internalUrl?: {
    locale: string;
    slug: string;
    trackingPageCategory: {
      title: string;
    };
  };
  urlParams?: string;
  externalUrl?: string;
}

interface ISubmenuCard {
  id: string;
  title: string;
  subtitle: string;
  tagText: string;
  image: IImage;
  urlParams?: string;
  __typename?: string;
  internalLink: {
    __typename?: string;
    locale: string;
    slug: string;
    trackingPageName: {
      title: string;
    };
  };
  anchor: {
    surtitle: string;
  };
}

interface ISubmenuSection {
  title: string;
  backgroundColor: PayFitColor;
  submenuItems: Array<ISubmenuItem>;
  cta: Array<ButtonV2Props>;
  submenuCard: Array<ISubmenuCard>;
}

interface ISubmenu {
  isOpen?: boolean;
  id: string;
  submenuSections: Array<ISubmenuSection>;
  className?: string;
  index: number;
  locale: string;
  submenuColumns: any[];
  sectionBottom: any;
}

export const Submenu = ({
  locale,
  id,
  submenuColumns,
  sectionBottom,
  index,
}: ISubmenu) => {
  const submenuRef = useRef<HTMLDivElement>(null);
  const screenSize = useScreenSize();

  useEffect(() => {
    const submenu = submenuRef.current; // Get the current submenu reference

    // Exit early if the submenu does not exist
    if (!submenu) return;

    // Find the closest parent element with the class 'menuItem'
    const parentWithOpenClass = submenu.closest('.menuItem');

    // Define a MutationObserver to monitor changes to the parent's class attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check if the changed attribute is the 'class'
        if (mutation.attributeName === 'class') {
          // Find the closest parent with the class 'open'
          const parentWithOpenClass = submenu.closest('.submenu-open');

          // If the parent has the 'open' class, calculate the submenu's position
          if (parentWithOpenClass) {
            const viewportWidth = window.innerWidth; // Get the viewport's width
            const submenuRect = submenu.getBoundingClientRect(); // Get submenu's position
            const parentRect = parentWithOpenClass.getBoundingClientRect(); // Get parent's position
            const originTranslate = parentRect.width / 2; // Calculate midpoint of parent
            const positionOrigin = parentRect.left + originTranslate; // Calculate the origin point

            // If the submenu overflows to the right of the viewport
            if (submenuRect.right > viewportWidth - 65) {
              const overflowToRight = window.innerWidth - positionOrigin;
              const overflowAmount = submenuRect.width - overflowToRight + 65;
              // Adjust the submenu position to stay 5px within the right edge
              submenu.style.transform = ['xl', 'xxl'].includes(screenSize || '')
                ? `translateX(${-overflowAmount}px)`
                : '';
            }
            // If the submenu overflows to the left of the viewport
            else if (submenuRect.left < 65) {
              const overflowToLeft = positionOrigin - 65;
              // Adjust the submenu position to stay 5px within the left edge
              submenu.style.transform = ['xl', 'xxl'].includes(screenSize || '')
                ? `translateX(-${overflowToLeft}px)`
                : '';
            }
          }
        }
      });
    });

    // Observe changes in attributes on the parent element
    if (parentWithOpenClass) {
      observer.observe(parentWithOpenClass, { attributes: true });
    }

    // Cleanup the observer when the component unmounts
    return () => {
      if (parentWithOpenClass) {
        observer.disconnect();
      }
    };
  }, [screenSize]);

  return (
    <>
      <div className={classNames('Submenu', 'relative')}>
        <div
          ref={submenuRef}
          className={classNames(
            'hidden',
            'xl:group-hover:block submenu-open:block',
            'xl:group-hover:animate-fade-in-300 submenu-open:animate-fade-in-300',
            'transition-all duration-[300ms]',
            'xl:absolute xl:-translate-x-1/2 xl:left-1/2',
            'pt-16 pb-40 xl:p-0 ',
          )}
          id={`submenu-${id}`}
        >
          <div
            className={classNames(
              'relative flex justify-start xl:justify-center',
              'xl:left-0 xl:z-10 xl:top-[18px]',
              'xl:shadow-m xl:rounded-[20px]',
            )}
          >
            <SubMenuContent
              {...{ submenuColumns, sectionBottom, locale, index }}
            />
          </div>
        </div>
      </div>
    </>
  );
};
